import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ExternalScriptLoaderService {
  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {}

  /**
   * Пробуем загрузить скрипт по primarySrc. Если печаль - пробуем fallbackSrc
   */
  async loadWithFallback(
    primarySrc: string,
    fallbackSrc: string,
    options?: { timeoutMs?: number; id?: string; insertIntoHead?: boolean; attributes?: Record<string, string> },
  ): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return Promise.resolve();

    const timeoutMs = options?.timeoutMs ?? 10_000;

    try {
      return await this.loadScript(primarySrc, { ...options, timeoutMs });
    } catch {
      return await this.loadScript(fallbackSrc, { ...options, timeoutMs });
    }
  }

  /**
   * Пытается загрузить скрипт. При ошибке или истечении timeout - reject.
   */
  private loadScript(
    src: string,
    options?: { timeoutMs?: number; id?: string; insertIntoHead?: boolean; attributes?: Record<string, string> },
  ): Promise<void> {
    const timeoutMs = options?.timeoutMs ?? 10_000;

    const existing = this.document.querySelector<HTMLScriptElement>(`script[src="${CSS.escape(src)}"]`);
    if (existing) return Promise.resolve();

    return new Promise<void>((resolve, reject) => {
      const script = this.document.createElement('script');
      script.src = src;
      script.async = true;

      if (options?.id) script.id = options.id;
      if (options?.attributes) {
        for (const [k, v] of Object.entries(options.attributes)) {
          script.setAttribute(k, v);
        }
      }

      const timer = window.setTimeout(() => {
        cleanup();
        script.remove();
        reject(new Error(`Loading script timed out after ${timeoutMs}ms: ${src}`));
      }, timeoutMs);

      const onLoad = (): void => {
        cleanup();
        resolve();
      };

      const onError = (): void => {
        cleanup();
        script.remove();
        reject(new Error(`Failed to load script: ${src}`));
      };

      const cleanup = (): void => {
        window.clearTimeout(timer);
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onError);
      };

      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', onError, { once: true });

      if (options?.insertIntoHead) {
        this.document.head.appendChild(script);
      } else {
        this.document.body.appendChild(script);
      }
    });
  }
}
